import React from 'react';
import { useNavigate } from 'react-router-dom';
import useAdminPermissions from '../hooks/useAdminPermissions';
import { C, RADIUS } from '../styles/theme';
import LoadingScreen from './LoadingScreen';

const PermissionGate = ({ permission, children }) => {
  const { can, isSuperAdmin, loading, loadError } = useAdminPermissions();
  const navigate = useNavigate();

  // While permissions resolve we must render SOMETHING — falling through would
  // hit the denial branch below, because can() is false until permissions load.
  //
  // This deliberately renders the SAME component ProtectedRoute shows while the
  // session resolves. Two gates run back to back on every admin route; drawing
  // two different screens made one load look like two. Reusing the one visual
  // means the handoff is invisible — the pulse simply continues.
  if (loading) return <LoadingScreen />;

  // "Could not read your permissions" is a different problem from "you do not
  // have permission", and showing the same screen for both turns a policy
  // misconfiguration into an unsolvable mystery. This branch names it.
  if (loadError) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '10px', padding: '24px' }}>
        <div style={{ fontSize: '44px' }}>⚠️</div>
        <h2 style={{ color: C.ink, margin: 0, fontSize: '19px', fontWeight: 700 }}>
          Couldn't load your permissions
        </h2>
        <p style={{ color: C.inkSoft, margin: 0, fontSize: '13px', textAlign: 'center', maxWidth: 420 }}>
          Your admin record could not be read, so access can't be confirmed. This
          is a configuration problem, not a permissions one.
        </p>
        <code style={{ color: C.inkFaint, fontSize: '11.5px', background: C.surfaceSunk, padding: '6px 10px', borderRadius: RADIUS.sm, border: `1px solid ${C.border}` }}>
          {loadError}
        </code>
        <button
          onClick={() => window.location.reload()}
          style={{ marginTop: '10px', padding: '10px 22px', background: C.primary, border: 'none', borderRadius: RADIUS.sm, color: '#ffffff', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  const denied =
    permission === 'super_admin' ? !isSuperAdmin : !can(permission);

  if (denied) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: C.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '10px',
          padding: '24px',
        }}
      >
        <div style={{ fontSize: '48px' }}>🔒</div>
        <h2 style={{ color: C.ink, margin: 0, fontSize: '19px', fontWeight: 700 }}>
          Access denied
        </h2>
        <p style={{ color: C.inkSoft, margin: 0, fontSize: '13px', textAlign: 'center' }}>
          You don't have permission to view this page.
        </p>
        <button
          onClick={() => navigate('/admin')}
          style={{
            marginTop: '10px',
            padding: '10px 22px',
            background: C.primary,
            border: 'none',
            borderRadius: RADIUS.sm,
            color: '#ffffff',
            fontSize: '13px',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          ← Back to dashboard
        </button>
      </div>
    );
  }

  return children;
};

export default PermissionGate;